<template>
	<div class="flex max-w-full items-end">
		<div class="flex items-center justify-center bg-white" :class="axisSpacingClass">
			<span
				class="bg-white font-bold uppercase text-teal-700 opacity-75"
				:class="axisTextClass"
				style="writing-mode: vertical-rl; text-orientation: upright; line-height: 1; letter-spacing: 0;"
			>
				Probabilidade
			</span>
		</div>

		<div class="inline-block">
			<div class="flex flex-col">
				<div v-for="row in matrix" :key="row.probability" class="flex flex-row">
					<div
						class="flex items-center justify-center border gray-300 break-words bg-white text-center font-bold"
						:class="[cellWidthClass, labelPaddingClass, labelTextClass]"
					>
						{{ row.label }}
					</div>

					<div
						v-for="cell in row.cells"
						:key="cell.impact"
						class="border gray-300 flex flex-col justify-center"
						:class="[cellWidthClass, cellPaddingClass]"
						:style="{ backgroundColor: cell.color }"
					>
						<div
							v-for="(value, iIndex) in cell.values"
							:key="iIndex"
							class="mb-1 rounded-sm bg-white text-center font-semibold uppercase text-black last:mb-0"
							:class="valueTextClass"
						>
							{{ value }}
						</div>
					</div>
				</div>

				<div class="flex flex-row">
					<div class="border gray-300 bg-white" :class="cellWidthClass"></div>
					<div
						v-for="impact in impacts"
						:key="impact.key"
						class="border gray-300 bg-white font-bold flex items-center justify-center"
						:class="[cellWidthClass, labelPaddingClass, labelTextClass]"
					>
						{{ impact.label }}
					</div>
				</div>
			</div>

			<div class="flex justify-center">
				<span class="mt-1 w-full bg-white text-center font-bold text-teal-700 opacity-75" :class="impactTextClass">IMPACTO</span>
			</div>
		</div>
	</div>
</template>

<script>
import { ref, computed, onMounted, watch  } from 'vue';

export default {
  props: {
    data: {
      type: Array,
      default: () => []
    },
    compact: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
	const localData = ref([...props.data]);

    const cellWidthClass = computed(() => props.compact ? 'w-10 sm:w-14 md:w-16 lg:w-24' : 'w-12 sm:w-16 md:w-24 lg:w-40');
    const labelPaddingClass = computed(() => props.compact ? 'px-0.5 py-1 sm:px-1.5 sm:py-1.5 lg:px-2 lg:py-2' : 'px-1 py-1.5 sm:px-2 sm:py-2');
    const cellPaddingClass = computed(() => props.compact ? 'px-0.5 py-1 sm:px-1.5 sm:py-1.5 lg:px-3 lg:py-2' : 'px-1 py-1.5 sm:px-2 sm:py-2 lg:px-4');
    const labelTextClass = computed(() => props.compact ? 'text-[7px] leading-tight sm:text-[10px] md:text-xs lg:text-sm' : 'text-[8px] leading-tight sm:text-[10px] md:text-xs lg:text-sm');
    const valueTextClass = computed(() => props.compact ? 'text-[7px] leading-tight break-words sm:text-[10px] md:text-xs lg:text-sm' : 'text-[8px] leading-tight break-words sm:text-[10px] md:text-xs lg:text-sm');
    const axisTextClass = computed(() => props.compact ? 'text-[7px] sm:text-[10px] md:text-xs' : 'text-[8px] sm:text-[10px] md:text-xs');
    const impactTextClass = computed(() => props.compact ? 'text-[10px] sm:text-xs md:text-sm lg:text-base' : 'text-xs sm:text-sm lg:text-base');
    const axisSpacingClass = computed(() => props.compact ? 'pr-0.5 pb-8 sm:pr-1 sm:pb-10 md:pb-12' : 'pr-1 pb-8 sm:pb-10 md:pb-12');
    const impacts = [
      { key: 'low', label: 'LEVE' },
      { key: 'medium', label: 'MÉDIO' },
      { key: 'high', label: 'MODERADO' },
      { key: 'very_high', label: 'SEVERO' }
    ];

    const matrix = ref([
      {
        probability: 'very_high',
        label: 'MUITO ALTA',
        cells: [
          { impact: 'low', color: '#fbbf24', values: [] },
          { impact: 'medium', color: '#f87171', values: [] },
          { impact: 'high', color: '#f87171', values: [] },
          { impact: 'very_high', color: '#f87171', values: [] }
        ]
      },
      {
        probability: 'high',
        label: 'ALTA',
        cells: [
          { impact: 'low', color: '#fef08a', values: [] },
          { impact: 'medium', color: '#fbbf24', values: [] },
          { impact: 'high', color: '#f87171', values: [] },
          { impact: 'very_high', color: '#f87171', values: [] }
        ]
      },
      {
        probability: 'medium',
        label: 'MÉDIA',
        cells: [
          { impact: 'low', color: '#a3e635', values: [] },
          { impact: 'medium', color: '#fef08a', values: [] },
          { impact: 'high', color: '#fbbf24', values: [] },
          { impact: 'very_high', color: '#f87171', values: [] }
        ]
      },
      {
        probability: 'low',
        label: 'BAIXA',
        cells: [
          { impact: 'low', color: '#a3e635', values: [] },
          { impact: 'medium', color: '#a3e635', values: [] },
          { impact: 'high', color: '#fef08a', values: [] },
          { impact: 'very_high', color: '#fbbf24', values: [] }
        ]
      }
    ]);

    const updateMatrix = () => {
      matrix.value.forEach(row => {
        row.cells.forEach(cell => {
          cell.values = [];
        });
      });
      localData.value.forEach(item => {
        const row = matrix.value.find(r => r.probability === item.probability);
        if (row) {
          const cell = row.cells.find(c => c.impact === item.impact);
          if (cell) {
            cell.values.push(item.label);
          }
        }
      });
    };

    onMounted(() => {
		updateMatrix();
    });

    watch(
      () => props.data,
      (newData) => {
        localData.value = [...newData];
        updateMatrix();
      },
      { deep: true }
    );

    return {
      impacts,
      matrix,
      cellWidthClass,
      labelPaddingClass,
      cellPaddingClass,
      labelTextClass,
      valueTextClass,
      axisTextClass,
      impactTextClass,
      axisSpacingClass
    };
  }
};
</script>
